import React, { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  Stack,
} from '@enechain/ecloud-designsystem'
import {
  MarginReportChart,
  type MarginReportChartInput,
  MarginReportTable,
  isHybridMarginReport,
} from '@enechain/eposition-ui'

import { useListOrganizationMarginReports } from '~/pages/MarginReport/hooks/useListOrganizationMarginReports'
import { convertEntriesToChartData } from '~/pages/MarginReport/utils/convertEntries'

// 期間切替時のクエリ再取得中に data が一瞬空になり isHybrid が false へ揺れて
// Chart 側の凡例選択がリセットされるのを防ぐため、data が空の間は直前の判定を
// 保持する（初回ロード中でまだ一度も非空になっていない場合のみ false）。
// 恒久的にデータが空（0件組織・エラー時）の場合も直前値を保持し続け、初回から
// 空であれば false のままとなる。
function useStableIsHybrid(data: MarginReportChartInput[]): boolean {
  const [stable, setStable] = useState(false)
  const next = data.length > 0 ? isHybridMarginReport(data) : stable
  if (next !== stable) {
    setStable(next)
  }
  return next
}

export const MarginReportPage: React.FC = (): React.ReactElement => {
  const { t } = useTranslation(['page'])
  const [period, setPeriod] = useState<30 | 90>(30)

  const breadcrumbItems = [
    { label: t('page:shared.margin_report'), bold: true },
  ]

  const { entries } = useListOrganizationMarginReports({ period })
  const { data, marginCallDates } = useMemo(
    () => convertEntriesToChartData(entries),
    [entries],
  )
  const isHybrid = useStableIsHybrid(data)

  return (
    <SideMenuLayoutContentLayout
      header={<SideMenuLayoutBreadcrumb items={breadcrumbItems} />}
    >
      <Stack gap="4">
        <MarginReportChart
          data={data}
          marginCallDates={marginCallDates}
          period={period}
          onPeriodChange={setPeriod}
          isHybrid={isHybrid}
        />
        <MarginReportTable data={data} period={period} isHybrid={isHybrid} />
      </Stack>
    </SideMenuLayoutContentLayout>
  )
}
